'use client';

/**
 * "Pick a story idea" (make-up-your-own spec §3.2): the idea cards both create flows show so a parent never starts
 * from a blank page. 6 cards (a radiogroup with arrow keys and one tab stop), then "Surprise me" (a plain button)
 * and "More ideas" for up to 12. Built on the design-system tokens
 * (the flows around it will be restyled onto them).
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Baby, Bug, Bus, Cake, Castle, ChefHat, Clock, Cloud, CloudSnow, Droplets, FerrisWheel, Flashlight, Gift, Map as MapIcon, Moon,
  Orbit, PartyPopper, Repeat, Rocket, Shuffle, Snowflake, Sparkles, Swords, Tent, TrainFront, Trees, Zap,
  type LucideIcon,
} from 'lucide-react';
import { IDEAS_MAX, IDEAS_SHOWN, type FilledIdea, type IdeaIcon } from '@/lib/catalogue/idea-cards';

const ICONS: Record<IdeaIcon, LucideIcon> = {
  moon: Moon, flashlight: Flashlight, droplets: Droplets, cloud: Cloud, 'party-popper': PartyPopper,
  snowflake: Snowflake, rocket: Rocket, 'ferris-wheel': FerrisWheel, orbit: Orbit, map: MapIcon, bug: Bug,
  'train-front': TrainFront, baby: Baby, bus: Bus, repeat: Repeat, gift: Gift, tent: Tent, zap: Zap, swords: Swords,
  cake: Cake, 'chef-hat': ChefHat, 'cloud-snow': CloudSnow, clock: Clock, trees: Trees, sparkles: Sparkles, castle: Castle,
};

const FOCUS =
  'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-marigold-deep';
const CARD = `flex min-h-11 w-full flex-col items-start gap-1.5 rounded-xl border p-3 text-left text-ink transition-colors disabled:opacity-60 ${FOCUS}`;
const CARD_OFF = 'border-line bg-paper-raised hover:border-marigold-deep';
const CARD_ON = 'border-ink bg-paper-raised ring-2 ring-marigold-deep';
const DAB = 'inline-flex h-8 w-8 items-center justify-center rounded-full bg-accent2-soft text-ink';

export interface IdeaCardPickerProps {
  cards: FilledIdea[];
  selectedId: string | null;
  onPick: (card: FilledIdea) => void;
  onSurprise: () => void;
  disabled?: boolean;
}

/**
 * Radiogroup arrow keys (WAI-ARIA radio pattern): the index a key moves to among `count` cards, wrapping; null for
 * any other key.
 */
export function ideaKeyTarget(key: string, index: number, count: number): number | null {
  if (count <= 0) return null;
  if (key === 'ArrowRight' || key === 'ArrowDown') return (index + 1) % count;
  if (key === 'ArrowLeft' || key === 'ArrowUp') return (index - 1 + count) % count;
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  return null;
}

export function IdeaCardPicker({ cards, selectedId, onPick, onSurprise, disabled }: IdeaCardPickerProps) {
  const all = cards.slice(0, IDEAS_MAX);
  const selectedIndex = all.findIndex((c) => c.id === selectedId);
  const [expanded, setExpanded] = useState(() => selectedIndex >= IDEAS_SHOWN);
  // Set by "More ideas": that button unmounts on expand, so the focus moves to the first new card
  const [focusFirstNew, setFocusFirstNew] = useState(false);
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);
  const showAll = expanded || selectedIndex >= IDEAS_SHOWN;
  const shown = showAll ? all : all.slice(0, IDEAS_SHOWN);
  // Roving tab stop: the checked card, else the first
  const tabStop = selectedIndex >= 0 && selectedIndex < shown.length ? selectedIndex : 0;

  useEffect(() => {
    if (!focusFirstNew || !showAll) return;
    buttons.current[IDEAS_SHOWN]?.focus();
    setFocusFirstNew(false);
  }, [focusFirstNew, showAll]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const to = ideaKeyTarget(e.key, index, shown.length);
    if (to === null) return;
    e.preventDefault();
    onPick(shown[to]);
    buttons.current[to]?.focus();
  };

  return (
    <div className="space-y-3" data-idea-picker="">
      <div className="space-y-1">
        <h3 className="text-lg font-semibold text-ink">Pick a story idea</h3>
        <p className="text-sm text-ink-soft">Tap one to use it. You can change the title and the idea after.</p>
      </div>
      <div role="radiogroup" aria-label="Story ideas" className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {shown.map((card, index) => {
          const Icon = ICONS[card.icon] ?? Sparkles;
          const on = card.id === selectedId;
          return (
            <button
              key={card.id}
              ref={(el) => { buttons.current[index] = el; }}
              type="button"
              role="radio"
              aria-checked={on}
              tabIndex={index === tabStop ? 0 : -1}
              disabled={disabled}
              onClick={() => onPick(card)}
              onKeyDown={(e) => onKeyDown(e, index)}
              className={`${CARD} ${on ? CARD_ON : CARD_OFF}`}
              data-idea-id={card.id}
            >
              <span className={DAB} aria-hidden="true"><Icon className="h-4 w-4" /></span>
              <span className="text-sm font-semibold leading-snug">{card.title}</span>
              <span className="line-clamp-3 text-xs leading-snug text-ink-soft">{card.idea}</span>
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {/* An action, not a choice: a plain button outside the radiogroup */}
        <button
          type="button"
          disabled={disabled}
          onClick={onSurprise}
          className={`inline-flex min-h-11 items-center gap-2 rounded-full border border-dashed px-4 py-2 text-left text-sm text-ink disabled:opacity-60 ${CARD_OFF} ${FOCUS}`}
          data-idea-id="surprise"
        >
          <Shuffle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="font-semibold">Surprise me</span>
          <span className="text-xs text-ink-soft">A new idea for your family</span>
        </button>
        {!showAll && all.length > IDEAS_SHOWN && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              setExpanded(true);
              setFocusFirstNew(true);
            }}
            className={`inline-flex min-h-11 items-center rounded-full border border-line bg-paper-raised px-4 text-sm font-medium text-ink hover:border-marigold-deep disabled:opacity-60 ${FOCUS}`}
          >
            More ideas
          </button>
        )}
      </div>
    </div>
  );
}
